import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../context/UserContext";
import {
  checkUsernameAvailability,
  setUsername as setUsernameApi,
  updateUserAccountTypeWithData,
} from "../utils/utils";
import {
  logLegacyUsernamePromptEvent,
  setLegacyUsernamePromptDismissed,
} from "../utils/legacyUsernamePrompt";

const USERNAME_REGEX = /^[a-z0-9_]+$/;

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export default function UsernameSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loadUserContext, userUsername: contextUsername } = useUser();

  const changeMode = location.state?.changeMode || false;
  const accountTypeName = location.state?.accountTypeName;
  const publicOfficeStatus = location.state?.public_office_status;
  const publicOfficeLevel = location.state?.public_office_level;
  const publicOfficePosition = location.state?.public_office_position;
  const claimedBioguideId = location.state?.claimed_bioguide_id;
  const fromSetupRep = location.state?.fromSetupRep === true;
  const returnToRepsView = location.state?.returnToRepsView === true;
  const setupRestore = location.state?.setupRestore;
  const fromDashboard = location.state?.fromDashboard === true;

  const existingUsername = contextUsername || "";

  const [username, setUsername] = useState(changeMode && existingUsername ? existingUsername : "");
  const [validationState, setValidationState] = useState(
    changeMode && existingUsername ? "available" : "idle"
  );
  const [validationMessage, setValidationMessage] = useState(
    changeMode && existingUsername ? "Your current username" : ""
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPrefilledUsername, setHasPrefilledUsername] = useState(changeMode && !!existingUsername);
  const [successMessage, setSuccessMessage] = useState(null);

  const debouncedUsername = useDebounce(username, 500);

  const handleBack = useCallback(() => {
    if (fromSetupRep) {
      navigate("/setup-representative", {
        state: {
          changeMode,
          accountTypeName,
          public_office_status: publicOfficeStatus,
          public_office_level: publicOfficeLevel,
          public_office_position: publicOfficePosition,
          showCalculatedReps: returnToRepsView,
          setupRestore: returnToRepsView ? setupRestore : undefined,
        },
      });
      return;
    }
    if (changeMode) {
      // Account type change flow — go back through browser history (to setup-rep or account management)
      if (window.history.length > 1) {
        navigate(-1);
        return;
      }
      navigate("/setup-representative");
      return;
    }
    if (fromDashboard) {
      navigate("/dashboard");
      return;
    }
    // Default onboarding flow (e.g. arrived via redirect without state)
    navigate("/setup-representative");
  }, [
    fromSetupRep,
    returnToRepsView,
    setupRestore,
    navigate,
    changeMode,
    fromDashboard,
    accountTypeName,
    publicOfficeStatus,
    publicOfficeLevel,
    publicOfficePosition,
  ]);

  // Pre-populate username in changeMode (handles async context loading)
  useEffect(() => {
    if (changeMode && existingUsername && !hasPrefilledUsername) {
      setUsername(existingUsername);
      setValidationState("available");
      setValidationMessage("Your current username");
      setHasPrefilledUsername(true);
    }
  }, [changeMode, existingUsername, hasPrefilledUsername]);

  useEffect(() => {
    if (!debouncedUsername) {
      setValidationState("idle");
      setValidationMessage("");
      return;
    }

    if (debouncedUsername.length < 3) {
      setValidationState("invalid");
      setValidationMessage("Username must be at least 3 characters.");
      return;
    }

    if (!USERNAME_REGEX.test(debouncedUsername)) {
      setValidationState("invalid");
      setValidationMessage(
        "Only lowercase letters, numbers, and underscores are allowed."
      );
      return;
    }

    // In changeMode, same as existing = valid without API call
    if (changeMode && debouncedUsername === existingUsername) {
      setValidationState("available");
      setValidationMessage("Your current username");
      return;
    }

    let cancelled = false;

    async function check() {
      setValidationState("checking");
      setValidationMessage("Checking availability…");

      try {
        const result = await checkUsernameAvailability(debouncedUsername);

        if (cancelled) return;

        if (result.available) {
          setValidationState("available");
          setValidationMessage("This username is available!");
        } else {
          setValidationState("taken");
          setValidationMessage(
            result.error || "This username is already taken."
          );
        }
      } catch {
        if (!cancelled) {
          setValidationState("invalid");
          setValidationMessage("Could not check availability. Try again.");
        }
      }
    }

    check();
    return () => {
      cancelled = true;
    };
  }, [debouncedUsername, changeMode, existingUsername]);

  const handleChange = useCallback((e) => {
    const raw = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setUsername(raw);
    setValidationState("idle");
    setValidationMessage("");
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (validationState !== "available" || isSubmitting) return;

      setIsSubmitting(true);
      try {
        if (changeMode) {
          // Save the account type data
          const payload = {
            account_type_name: accountTypeName,
          };
          if (publicOfficeStatus) payload.public_office_status = publicOfficeStatus;
          if (publicOfficeLevel) payload.public_office_level = publicOfficeLevel;
          if (publicOfficePosition) payload.public_office_position = publicOfficePosition;
          if (claimedBioguideId) payload.claimed_bioguide_id = claimedBioguideId;

          await updateUserAccountTypeWithData(payload);
        }

        // Only update username if it's different from existing
        if (!changeMode || username !== existingUsername) {
          await setUsernameApi(username);
        }

        setLegacyUsernamePromptDismissed(false);
        logLegacyUsernamePromptEvent("username_saved", {
          surface: "web_username_selection",
          changeMode,
        });
        await loadUserContext();

        if (changeMode) {
          navigate("/dashboard", { replace: true });
          setSuccessMessage("Your account type has been updated!");
          setTimeout(() => setSuccessMessage(null), 5000);
        } else {
          navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        setValidationState("invalid");
        setValidationMessage(
          err.response?.data?.error || err.response?.data?.detail || err.message || "Failed to save changes."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validationState, isSubmitting, username, loadUserContext, navigate,
      changeMode, existingUsername, accountTypeName,
      publicOfficeStatus, publicOfficeLevel, publicOfficePosition, claimedBioguideId,
    ]
  );

  const validationIcon = (() => {
    switch (validationState) {
      case "checking":
        return (
          <svg
            className="h-5 w-5 animate-spin text-[#047857]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        );
      case "available":
        return (
          <svg
            className="h-5 w-5 text-emerald-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "taken":
      case "invalid":
        return (
          <svg
            className="h-5 w-5 text-red-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
              clipRule="evenodd"
            />
          </svg>
        );
      default:
        return null;
    }
  })();

  const messagColor =
    validationState === "available"
      ? "text-emerald-600"
      : validationState === "taken" || validationState === "invalid"
      ? "text-red-600"
      : validationState === "checking"
      ? "text-[#047857]"
      : "text-gray-600";

  const inputBorderClass =
    validationState === "available"
      ? "border-emerald-500 focus:border-emerald-500 focus:ring-emerald-500"
      : validationState === "taken" || validationState === "invalid"
      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
      : "border-gray-200 focus:border-[#047857] focus:ring-[#047857]";

  const canSubmit = validationState === "available" && !isSubmitting;

  const getButtonText = () => {
    if (isSubmitting) return null; // spinner shown
    if (changeMode) {
      return username !== existingUsername ? "Save New Username" : "Confirm & Finish";
    }
    return "Continue";
  };

  const showBackButton = true;

  return (
    <div className="min-h-screen bg-white flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        {successMessage && (
          <div className="mb-6 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-semibold text-emerald-800">
            {successMessage}
          </div>
        )}

        {showBackButton && (
          <button
            type="button"
            onClick={handleBack}
            className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 hover:text-[#047857] transition-colors"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        )}

        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {changeMode ? "Confirm your username" : "Choose your username"}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="relative">
            <span
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 select-none text-lg font-semibold text-[#047857]"
              aria-hidden
            >
              @
            </span>
            <input
              id="username"
              type="text"
              value={username}
              onChange={handleChange}
              aria-label={changeMode ? "Current username" : "Username"}
              placeholder="your_username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={30}
              className={`w-full rounded-xl border-2 bg-gray-50 py-3 pl-9 pr-12 text-base text-gray-900 placeholder:text-gray-600 outline-none focus:ring-1 transition-colors ${inputBorderClass}`}
            />
            <div className="pointer-events-none absolute right-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center">
              {validationIcon}
            </div>
          </div>

          <div className="mt-2 min-h-[1.25rem]">
            {validationMessage ? (
              <p className={`text-sm font-medium ${messagColor}`}>
                {validationMessage}
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Letters, numbers, and underscores only
              </p>
            )}
          </div>

          <div className="mt-1 text-right">
            <span className="text-xs text-gray-600">
              {username.length}/30 characters
            </span>
          </div>

          {changeMode && (
            <p className="mt-4 text-sm text-gray-900">
              {username === existingUsername
                ? "Your username will remain the same"
                : "Edit the field above if you'd like a different username"}
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className={`mt-8 flex w-full items-center justify-center rounded-xl px-4 py-3 text-base font-bold text-white transition-colors ${
              canSubmit
                ? "bg-[#047857] hover:bg-[#065f46] active:bg-[#064e3b]"
                : "cursor-not-allowed bg-gray-300 text-gray-600"
            }`}
          >
            {isSubmitting ? (
              <svg
                className="h-5 w-5 animate-spin text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : (
              getButtonText()
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
